import { createContext, useCallback, useContext, useEffect, useRef, useState, type ReactNode } from "react";
import {
  listImagingSeries,
  getImagingBatchStatus,
  listActiveImagingBatches,
  getSeriesGroupId,
  IMAGING_STUDIES_PAGE_SIZE,
  type ImagingSeries,
  type ImagingBatchStatus,
} from "@/portals/patient/api/imagingStudiesApi";
import { useInfiniteList } from "@/lib/query/useInfiniteList";
import { STALE } from "@/lib/query/queryClient";
import { patientKeys } from "@/portals/patient/api/patientQueries";
import { startBackoffPolling, type BackoffPollerHandle } from "@/shared/lib/pollingBackoff";
import { UPLOAD_STATUS_POLL_MS, POLL_BACKOFF_MAX_MS } from "@/features/patient/records/utils/pollingConfig";
import { toastError } from "@/shared/lib/toast";
import { useFamily } from "./FamilyContext";

/**
 * One DICOM batch that's been submitted (POST .../upload accepted it — see
 * ImagingUploadModal.tsx) but hasn't reached a terminal status yet. The
 * modal itself no longer tracks this at all: it registers the batch here the
 * instant it's accepted, then closes immediately (mirroring
 * RecordsUploadModal's own "close on submit, watch progress elsewhere"
 * shape) — this context polls it to completion regardless of whether the
 * modal, or even the page that opened it, is still mounted. See
 * registerActiveBatch below.
 */
export interface ActiveImagingBatch {
  batchId: string;
  /** What to show in place of a filename — the modal's own "Image Title"
   *  field if the patient entered one, else the single file's name, else
   *  "N files" for a multi-file batch. Decided once, at registration time. */
  displayName: string;
  totalExpected: number;
  /** Grows monotonically while status is "queued"/"processing" — this entry
   *  is removed the moment status becomes "completed"/"failed", so a
   *  consumer never actually sees processedCount reach totalExpected; it
   *  just sees the row disappear (and, for "completed", the real `series`
   *  list refresh with the new studies already in it). */
  processedCount: number;
  status: ImagingBatchStatus;
}

interface ImagingStudiesContextValue {
  /** Every SERIES fetched so far across all loaded pages (see Phase 2 —
   *  the list is grouped by series_instance_uid, one entry per series, not
   *  per raw uploaded file any more) — the "Recent DICOM Uploads" card shows
   *  the first 3 of these, the full scrollable list below shows all of
   *  them. */
  series: ImagingSeries[];
  isLoading: boolean;
  /** True while loadMore's fetch is in flight — drives the scroll-to-load
   *  spinner (see InfiniteScrollContainer). */
  isLoadingMore: boolean;
  /** True if more studies exist past `studies` — fetch 10 more via loadMore
   *  when the list is scrolled near its end. */
  hasMore: boolean;
  error: string | null;
  /** The patient's real total SERIES count across every page (a genuine
   *  COUNT(*) from the backend, not just `series.length`) — what the
   *  Diagnostics page's "Imaging Studies" stat tile should show. */
  totalCount: number;
  /** Fetches and appends the next page of series. */
  loadMore: () => void;
  /** Re-fetches the first page immediately — called after a successful
   *  upload/delete so the list reflects it without a manual page reload,
   *  the same "instant reactivity" convention every other fetch-once data
   *  source in this app follows (see ParsedRecordsContext/FamilyContext's
   *  own refresh()). Also called automatically by this context itself once
   *  a batch registered via registerActiveBatch reaches a terminal status —
   *  see below. */
  refresh: () => void;
  /** The same ref-based cache-get/cache-set pattern UploadHistoryContext
   *  already uses for document files — reused here, not reinvented, just
   *  keyed by a series MEMBER's imaging_study id and holding the raw file
   *  Blob. Reopening the DICOM viewer for the same member file reuses the
   *  cached bytes instead of refetching from S3. */
  getCachedFileBlob: (memberId: string) => Blob | undefined;
  cacheFileBlob: (memberId: string, blob: Blob) => void;
  /** DICOM batches submitted but not yet finished — see ActiveImagingBatch.
   *  Rendered as distinct "still working" rows by RecentDicomUploadsCard,
   *  above the normal completed-series rows. */
  activeBatches: ActiveImagingBatch[];
  /** Called by ImagingUploadModal the instant a batch upload POST is
   *  accepted (before the modal closes) — starts this context polling
   *  GET .../batches/{batchId} on its own backoff-timer, independent of
   *  whether the modal or the page that opened it stays mounted. Resolves
   *  itself once the batch reaches "completed"/"failed": removes the entry
   *  from `activeBatches` and calls `refresh()` so any newly created
   *  studies appear without a manual reload; a "failed" batch also fires a
   *  toast (see toastError below) so the failure isn't silent. A no-op if
   *  this exact batchId is already being tracked. */
  registerActiveBatch: (
    batchId: string,
    displayName: string,
    totalExpected: number,
    actingAsProfileId?: string,
  ) => void;
}

const ImagingStudiesContext = createContext<ImagingStudiesContextValue | null>(null);

/**
 * Paginated (10 at a time — see IMAGING_STUDIES_PAGE_SIZE) provider for the acting patient's uploaded DICOM studies (GET
 * /api/patient/imaging-studies) — patient-wide, not scoped to any single
 * encounter/record, matching exactly how the backend itself scopes this
 * resource (get_effective_patient_id_for_viewing has no per-encounter
 * concept). Re-fetches whenever the patient switches which profile they're
 * viewing (see FamilyContext.activeDependent), same as every other
 * per-profile data source in this app.
 *
 * This used to be a plain hook (useImagingStudies, called directly from
 * DiagnosticsImagingView) whose own docstring claimed "fetch-once-per-
 * session" — that was only ever true while DiagnosticsImagingView itself
 * stayed mounted. It's rendered from DiagnosticsPage, a route: navigating
 * away and back unmounts and remounts it, which threw away the hook's
 * entire state (studies, totalCount, the file-blob cache) and refetched
 * from scratch every single visit — the exact bug this Provider fixes, by
 * mounting once in PatientProtectedLayout.tsx instead of inside the route
 * that comes and goes. Same fix shape as UploadHistoryContext/FamilyContext
 * already use for the same reason.
 *
 * The series list itself lives in the shared query cache (useInfiniteList:
 * offset paging, hasMore, dedup-on-append), keyed by profile — a revisit
 * shows every loaded page at once and revalidates in the background.
 *
 * ACTIVE BATCH TRACKING (see ActiveImagingBatch/registerActiveBatch above):
 * this is also now where an in-flight DICOM batch's progress lives, for
 * exactly the same "mounted once, survives navigation" reason the series
 * list itself lives here rather than in a component. ImagingUploadModal used
 * to poll this itself and block on a "Processing X of Y" screen until the
 * batch finished — moved here so the modal can close the instant its POST
 * is accepted (matching RecordsUploadModal's own upload flow) while
 * progress keeps polling regardless of whether the modal (or even the page
 * that opened it) is still mounted.
 */
export function ImagingStudiesProvider({ children }: { children: ReactNode }) {
  const { activeDependent } = useFamily();
  const actingAsProfileId = activeDependent?.id;

  const [activeBatches, setActiveBatches] = useState<ActiveImagingBatch[]>([]);
  const batchPollersRef = useRef<Record<string, BackoffPollerHandle>>({});

  const fileBlobCacheRef = useRef<Record<string, Blob>>({});
  const getCachedFileBlob = useCallback((studyId: string) => fileBlobCacheRef.current[studyId], []);
  const cacheFileBlob = useCallback((studyId: string, blob: Blob) => {
    fileBlobCacheRef.current = { ...fileBlobCacheRef.current, [studyId]: blob };
  }, []);

  // A cached blob is only ever valid for the profile it was fetched under —
  // cleared on profile switch so a Dedicated Profile's studies never risk
  // resolving through a stale entry.
  useEffect(() => {
    fileBlobCacheRef.current = {};
  }, [actingAsProfileId]);

  const queryKey = patientKeys.imaging(actingAsProfileId);
  const fetchPage = useCallback(
    (offset: number, limit: number) => listImagingSeries(actingAsProfileId, { offset, limit }),
    [actingAsProfileId],
  );

  const {
    items: series,
    firstPage,
    isLoading,
    isLoadingMore,
    hasMore,
    error,
    loadMore,
    refresh,
  } = useInfiniteList({
    queryKey,
    fetchPage,
    pageSize: IMAGING_STUDIES_PAGE_SIZE,
    // NOT seriesInstanceUid — two distinct groups in the response can share
    // that raw tag (see getSeriesGroupId's own docstring); deduping on it
    // would wrongly drop a later page's genuinely-distinct group.
    getId: getSeriesGroupId,
    // Only changes through the patient's own imaging uploads, which refresh it.
    staleTime: STALE.slow,
    errorMessage: "Couldn't load your imaging studies. Please try again later.",
  });
  const totalCount = firstPage?.totalCount ?? 0;

  /** Starts (or, if batchId is already tracked, no-ops) a backoff poll of
   *  GET .../batches/{batchId} — same startBackoffPolling primitive
   *  UploadHistoryContext's own pollUploadStatus uses, for the identical
   *  reason (backing off on connectivity hiccups instead of retrying a
   *  struggling connection at full rate; see pollingBackoff.ts). */
  const registerActiveBatch = useCallback(
    (batchId: string, displayName: string, totalExpected: number, batchActingAsProfileId?: string) => {
      if (batchPollersRef.current[batchId]) return;

      setActiveBatches((prev) => [...prev, { batchId, displayName, totalExpected, processedCount: 0, status: "queued" }]);

      let poller: BackoffPollerHandle;
      poller = startBackoffPolling({
        intervalMs: UPLOAD_STATUS_POLL_MS,
        maxIntervalMs: POLL_BACKOFF_MAX_MS,
        poll: async () => {
          const status = await getImagingBatchStatus(batchId, batchActingAsProfileId);

          if (status.status === "queued" || status.status === "processing") {
            setActiveBatches((prev) =>
              prev.map((b) =>
                b.batchId === batchId
                  ? { ...b, processedCount: status.processed_count, totalExpected: status.total_expected, status: status.status }
                  : b,
              ),
            );
            return;
          }

          // Terminal ("completed" or "failed") — stop polling and drop the
          // row regardless of which: a "failed" batch can still have gotten
          // a handful of files through before the rest failed (same
          // "partial success still counts" rule the old in-modal summary
          // used — see ImagingUploadModal's git history), so refreshing the
          // real list either way is what actually gets any such study to
          // show up without a manual reload; refreshing on a genuine
          // zero-success failure is just a harmless no-op fetch.
          poller.stop();
          delete batchPollersRef.current[batchId];
          setActiveBatches((prev) => prev.filter((b) => b.batchId !== batchId));
          refresh();

          if (status.status === "failed") {
            // A failed batch otherwise vanishes from activeBatches with no
            // trace at all — this is the one visible signal the patient
            // gets that it didn't just quietly finish. Reuses the app's
            // existing dismissible-toast convention (same toastError every
            // other background failure in this app surfaces through)
            // rather than inventing a new "failed" row state that would
            // need its own dismiss affordance.
            toastError(`"${displayName}" failed to process. Please check the file(s) and try again.`);
          }
        },
      });
      batchPollersRef.current[batchId] = poller;
      if (document.visibilityState === "hidden") poller.pause();
    },
    [refresh],
  );

  // Batch polls pause while the tab is hidden and catch up at once when it's
  // visible again, like the upload polls (connection plan step 4).
  useEffect(() => {
    const onVisibilityChange = () => {
      for (const poller of Object.values(batchPollersRef.current)) {
        if (document.visibilityState === "visible") poller.resume();
        else poller.pause();
      }
    };
    document.addEventListener("visibilitychange", onVisibilityChange);
    return () => document.removeEventListener("visibilitychange", onVisibilityChange);
  }, []);

  // A batch belongs to whichever profile was active when it was submitted —
  // never meaningful to keep showing (or polling) it once the patient
  // switches to a different profile, same reasoning as the file-blob cache
  // clear above. Also stops every in-flight poller on unmount (logout).
  useEffect(() => {
    return () => {
      Object.values(batchPollersRef.current).forEach((p) => p.stop());
      batchPollersRef.current = {};
      setActiveBatches([]);
    };
  }, [actingAsProfileId]);

  // RESUME ON MOUNT (or profile switch): a batch tracked only in
  // `activeBatches` (pure in-memory React state) is lost the instant this
  // provider unmounts — a full page reload, or a sign-out followed by
  // sign-in (PatientProtectedLayout renders either this whole provider tree
  // or a <Navigate> to /patient/auth/signin at the same position depending
  // on isAuthenticated, so flipping that flag unmounts this provider
  // outright and a subsequent sign-in mounts a brand new one — confirmed by
  // reading that file, not assumed) — even though the backend worker itself
  // is still processing the batch with no idea the browser ever left. This
  // re-fetches whatever's still non-terminal server-side and hands each one
  // to the SAME registerActiveBatch a fresh submission uses, so a resumed
  // batch gets identical live-progress treatment (same poller, same "Recent
  // DICOM Uploads" row) with no new code path to maintain. Re-runs on
  // actingAsProfileId change for the same reason fetchPage above does — a
  // resumed batch belongs to whichever profile was active when it was
  // submitted, never the one the patient has since switched to.
  //
  // CONFIRMED against the real backend (imaging_studies/router.py's
  // GET /batches) — see listActiveImagingBatches's own docstring for the
  // reconciliation. `displayName` falls back to "N files" from
  // total_expected when title is absent, matching the backend's own
  // documented display-name contract exactly ("title, else '{total_expected}
  // files'" — there's no per-file filename available at this list level to
  // fall back to first, unlike ImagingUploadModal's own richer fallback).
  useEffect(() => {
    let cancelled = false;

    (async () => {
      try {
        const resumable = await listActiveImagingBatches(actingAsProfileId);
        if (cancelled) return;
        for (const batch of resumable.data) {
          // Defense in depth alongside the endpoint's own ?status= filter —
          // a batch that finished in the gap between being listed and this
          // running must never be resumed as if still active.
          if (batch.status !== "queued" && batch.status !== "processing") continue;
          const displayName =
            (batch.title && batch.title.trim()) ||
            (batch.total_expected === 1 ? "1 file" : `${batch.total_expected} files`);
          registerActiveBatch(batch.batch_id, displayName, batch.total_expected, actingAsProfileId);
        }
      } catch {
        // Best-effort resume only — a failure here (network hiccup, the
        // endpoint not deployed yet, ...) must never block the rest of this
        // provider; the real series list already has its own error state.
        // An in-flight batch just won't show live progress until the next
        // reload or profile switch retries this.
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [actingAsProfileId, registerActiveBatch]);

  return (
    <ImagingStudiesContext.Provider
      value={{
        series, isLoading, isLoadingMore, hasMore, error, totalCount, loadMore, refresh,
        getCachedFileBlob, cacheFileBlob, activeBatches, registerActiveBatch,
      }}
    >
      {children}
    </ImagingStudiesContext.Provider>
  );
}

export function useImagingStudies(): ImagingStudiesContextValue {
  const ctx = useContext(ImagingStudiesContext);
  if (!ctx) throw new Error("useImagingStudies must be used within ImagingStudiesProvider");
  return ctx;
}
